/**
 * Shared request helpers for the api-client react hooks.
 *
 * Internal module — NOT part of the public `@barel/api-client/react`
 * surface (the barrel does not re-export it). Each domain module imports
 * the helpers it needs from here.
 */
import { getClientBaseUrl, getClientFetch, parseApiError, type ApiClient } from '../hc';

/**
 * Better Auth's catch-all handler (`apps/api/src/routes/auth.ts`) doesn't
 * declare typed shapes on the Hono router, so `hc<AppType>` doesn't know
 * about most `/auth/*` paths. We fall back to a typed JSON helper that
 * still rides on the *same wrapped fetch* the typed client uses (cookies,
 * correlation-id, `onError` — all preserved).
 */
export async function authFetch<T>(client: ApiClient, path: string, init: RequestInit): Promise<T> {
  const baseUrl = getClientBaseUrl(client);
  const url = `${baseUrl}${path}`;
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  const wrappedFetch = getClientFetch(client);
  const response = await wrappedFetch(url, { ...init, headers });
  if (!response.ok) {
    throw await parseApiError(response);
  }
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  const text = await response.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as T;
  }
}

export function postJson<T>(client: ApiClient, path: string, body: unknown): Promise<T> {
  return authFetch<T>(client, path, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
}

export function getJson<T>(client: ApiClient, path: string): Promise<T> {
  return authFetch<T>(client, path, { method: 'GET' });
}

export function deleteJson<T>(client: ApiClient, path: string, body?: unknown): Promise<T> {
  return authFetch<T>(client, path, {
    method: 'DELETE',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function patchJson<T>(client: ApiClient, path: string, body: unknown): Promise<T> {
  return authFetch<T>(client, path, {
    method: 'PATCH',
    body: JSON.stringify(body ?? {}),
  });
}
